import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import {DocumentNode} from 'graphql';
import React, {useEffect, useMemo} from 'react';

import NoteActions from 'actions/NoteActions';
import {TagSchema} from 'constants/RegistryConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import MetadataTableHeader from 'metadata/MetadataTableHeader';
import NoteStore from 'notebook/bioentities/NoteStore';
import {EditorPropertiesContext, NoteContext} from 'notebook/notes/core-ui/NoteContext';
import shallowCopyNote from 'notebook/notes/shallowCopyNote';
import GeneralNoteView from 'notebook/notes/tabs/GeneralNoteView';
import {GeneralNoteViewProps} from 'notebook/notes/tabs/NoteViewProps';
import isConfiguration from 'notebook/util/isConfiguration';
import {
  MixtureInstructionsQuery,
  MixtureInstructionsQueryVariables,
} from 'registry/mixtures/__generated/MixtureInstructionsQuery';
import * as MixtureActions from 'registry/mixtures/MixtureActions';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import * as logging from 'util/Logging';

type MixtureInstructionsGQL = NonNullable<
  ExtractByTypename<MixtureInstructionsQuery['mixture'], 'MixtureNode'>['instructions']
>;

export const MIXTURE_INSTRUCTIONS_QUERY: DocumentNode = gql`
  query MixtureInstructionsQuery($mixtureId: StrictString!) @skipAudit @engComponent(name: REGISTRY) {
    mixture(id: $mixtureId) {
      ... on MixtureNode {
        ...MixtureWithInstructionsGraphQL
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('MixtureWithInstructions').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface PresentationalProps {
  instructions?: MixtureInstructionsGQL | null;
  isLoading?: boolean;
}

interface Props {
  mixtureId: string;
  tagSchema: TagSchema;
  instructionsEditorProps: GeneralNoteViewProps;
}

export function MixtureInstructions(props: Props & PresentationalProps): JSX.Element {
  const {instructions} = props;

  useEffect(() => {
    if (instructions != null && !NoteStore.inStore(instructions.id)) {
      NoteActions.load(instructions.id, shallowCopyNote(instructions.noteJson));
    }
  }, [instructions]);

  const editorPropertiesContext = useMemo(
    () => ({
      showFormulas: false,
      fileId: instructions?.id,
      isConfig: instructions ? isConfiguration(instructions.id) : false,
    }),
    [instructions]
  );

  const handleCreateMixtureInstructions = (): void => {
    MixtureActions.initializeInstructions(props.mixtureId);
  };

  const maybeRenderInsertButton = (): JSX.Element | null => {
    if (props.isLoading || instructions != null) {
      return null;
    }
    return (
      <Tooltip title="Create instructions for this mixture">
        <Button
          className={css(styles.insertButton)}
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          onClick={() => {
            handleCreateMixtureInstructions();
          }}
          size={ButtonSize.MINI}
          text="Insert"
          dataTestAttrs={{component: 'MixtureInstructions', element: 'insertButton'}}
        />
      </Tooltip>
    );
  };

  const renderNoteEditor = (): JSX.Element => (
    // eslint-disable-next-line react/jsx-no-constructed-context-values -- Disabling for now, but worth fixing
    <NoteContext.Provider value={{}}>
      <EditorPropertiesContext.Provider value={editorPropertiesContext}>
        <GeneralNoteView {...props.instructionsEditorProps} fileId={instructions!.id} />
      </EditorPropertiesContext.Provider>
    </NoteContext.Provider>
  );

  return (
    <div
      className={classNames('metadata-table simpleDivTable', css(styles.instructionsBottomMargin))}
      {...getTestProps({component: 'MixtureInstructions', element: 'root'})}
    >
      <MetadataTableHeader
        isEditable={false}
        title={`${props.tagSchema.name} Instructions`}
        headerContent={null}
        customButton={maybeRenderInsertButton()}
      />
      {props.isLoading && (
        <div className={css(styles.loadingThrobber)}>
          <HorizontalThrobber />
        </div>
      )}
      {/* useEffect handles loading the instructions note in the NoteStore if it's not there yet */}
      {instructions != null && NoteStore.inStore(instructions.id) && renderNoteEditor()}
    </div>
  );
}

export default function QueryingMixtureInstructions(props: Props): JSX.Element {
  return (
    <BenchlingQuery<MixtureInstructionsQuery, MixtureInstructionsQueryVariables>
      query={MIXTURE_INSTRUCTIONS_QUERY}
      variables={{mixtureId: props.mixtureId}}
      renderLoading={() => <MixtureInstructions isLoading={true} {...props} />}
    >
      {(data) => {
        if (isInaccessible(data.mixture)) {
          logging.warn(`Expected mixture to be accessible: ${props.mixtureId}`);
          return null;
        } else {
          return <MixtureInstructions {...props} instructions={data.mixture.instructions} />;
        }
      }}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  instructionsBottomMargin: {
    marginBottom: '15px',
  },
  loadingThrobber: {
    marginTop: '10px',
    marginBottom: '10px',
    marginLeft: '15px',
    marginRight: '15px',
  },
  insertButton: {
    marginBottom: '10px',
  },
});
